import { useEffect, useRef, useState } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Switch,
  IconButton,
  Chip,
  Typography,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { colors } from "../../../../theme";

const ALLOWED_COLOR = "#4cceac";
const DENIED_COLOR = "#ff4444";

const EMPTY_FORM = { zone_a: "", zone_b: "", service: "", allowed: true, direction: "both", note: "" };

const DIRECTION_ICON = { both: SyncAltIcon, a_to_b: ArrowForwardIcon, b_to_a: ArrowBackIcon };

// Riporta zone_a/zone_b/direction a una forma canonica (coppia ordinata
// alfabeticamente + direzione riletta rispetto a quell'ordine), così due
// regole scritte con zone_a/zone_b scambiati risultano comunque comparabili
// -- serve solo per il controllo di conflitto qui sotto, la regola resta
// salvata con l'ordine "from/to" scelto dall'utente.
const canonicalPair = (zoneA, zoneB, direction) => {
  const [lo, hi] = [zoneA, zoneB].sort();
  if (zoneA === lo) return { lo, hi, dir: direction || "both" };
  const flipped = direction === "a_to_b" ? "b_to_a" : direction === "b_to_a" ? "a_to_b" : "both";
  return { lo, hi, dir: flipped };
};

// "both" copre entrambe le direzioni, quindi va in conflitto sia con
// a_to_b sia con b_to_a; a_to_b e b_to_a tra loro invece NON si
// sovrappongono (riguardano sensi di marcia opposti, coesistere è normale).
const directionsOverlap = (d1, d2) => d1 === "both" || d2 === "both" || d1 === d2;

// Una regola è in conflitto con un'altra se dichiarano stati opposti
// (allowed/not allowed) per la STESSA coppia di zone, STESSO servizio e una
// direzione che si sovrappone -- es. due regole "A → B, ssh" una allowed e
// l'altra not allowed. Non considera in conflitto un servizio specifico
// contro una regola generica (service vuoto = "qualsiasi"): è la normale
// gerarchia "specifico sovrascrive generico" delle policy di rete, non una
// contraddizione.
const findConflictingRule = (rules, form, editingId) => {
  const target = canonicalPair(form.zone_a.trim(), form.zone_b.trim(), form.direction);
  const service = (form.service || "").trim();
  return (rules || []).find((r) => {
    if ((r._id ?? r.id) === editingId) return false;
    if ((r.service || "").trim() !== service) return false;
    const other = canonicalPair(r.zone_a, r.zone_b, r.direction);
    if (other.lo !== target.lo || other.hi !== target.hi) return false;
    if (!directionsOverlap(other.dir, target.dir)) return false;
    return !!r.allowed !== !!form.allowed;
  });
};

const SectionLabel = ({ children }) => (
  <Typography
    sx={{
      fontSize: 11,
      fontWeight: 700,
      color: "#459095",
      textTransform: "uppercase",
      letterSpacing: 0.8,
      mb: 1,
    }}
  >
    {children}
  </Typography>
);

// Stesso stile/UX di ZoneManagerDialog (card, non tabelle) applicato alle
// regole "conduit permesso" zona-a-zona -- TASK-AI-031. Queste regole sono
// una policy DICHIARATA in dashboard, non ancora sincronizzata verso Orion
// (nessun push verso config.zeek esiste ancora): i conduit realmente
// osservati mostrati in "Network Zones" restano governati dall'allow-list
// che Orion ha già sul sensore. Vedi searchZoneConduits (source: "declared"
// vs "actual").
const ConduitRuleManagerDialog = ({ open, onClose, zoneNames, rules, onCreateRule, onUpdateRule, onDeleteRule }) => {
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [conflict, setConflict] = useState(null);
  const formSectionRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setEditingId(null);
      setForm(EMPTY_FORM);
      setConflict(null);
    }
  }, [open]);

  // Il conflitto è ricalcolato ad ogni modifica del form (non solo al
  // submit) così l'utente vede subito perché non può salvare, invece di
  // scoprirlo solo cliccando "Save".
  useEffect(() => {
    if (!form.zone_a.trim() || !form.zone_b.trim()) { setConflict(null); return; }
    setConflict(findConflictingRule(rules, form, editingId));
  }, [rules, form, editingId]);

  const startEdit = (rule) => {
    setEditingId(rule._id ?? rule.id);
    setForm({
      zone_a: rule.zone_a ?? "",
      zone_b: rule.zone_b ?? "",
      service: rule.service ?? "",
      allowed: rule.allowed ?? true,
      direction: rule.direction ?? "both",
      note: rule.note ?? "",
    });
    formSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = () => {
    if (!form.zone_a.trim() || !form.zone_b.trim()) return;
    if (findConflictingRule(rules, form, editingId)) return; // blocked, see the inline warning
    if (editingId) onUpdateRule(editingId, form);
    else onCreateRule(form);
    cancelEdit();
  };

  const handleDelete = (id) => {
    if (editingId === id) cancelEdit();
    onDeleteRule(id);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { backgroundColor: "#161616", backgroundImage: "none" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, borderBottom: `1px solid ${colors.grey[800]}`, pb: 1.5 }}>
        <SyncAltIcon sx={{ color: "#459095", fontSize: 22 }} />
        <Box>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: colors.grey[100] }}>Conduit Rules</Typography>
          <Typography sx={{ fontSize: 12, color: colors.grey[400], mt: 0.2 }}>
            Declare which zone-to-zone conduits should be allowed (IEC 62443). Not yet synced to Orion.
          </Typography>
        </Box>
        <Chip
          label={`${rules.length} rule${rules.length === 1 ? "" : "s"}`}
          size="small"
          sx={{ ml: "auto", bgcolor: "#45909522", color: "#459095", fontSize: 11, fontWeight: 700 }}
        />
      </DialogTitle>

      <DialogContent sx={{ backgroundColor: "#161616", pt: 3 }}>
        <Box sx={{ mb: 2.5 }}>
          <SectionLabel>Declared Rules</SectionLabel>
          {rules.length === 0 ? (
            <Typography sx={{ color: colors.grey[500], fontSize: 12.5, fontStyle: "italic" }}>
              No rules yet.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {rules.map((r) => {
                const id = r._id ?? r.id;
                const editing = editingId === id;
                return (
                  <Box
                    key={id}
                    sx={{
                      p: 1.2,
                      borderRadius: 1.5,
                      backgroundColor: "#1c1c1c",
                      border: `1px solid ${editing ? "#459095" : colors.grey[800]}`,
                      borderLeft: `3px solid ${r.allowed ? ALLOWED_COLOR : DENIED_COLOR}`,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      "&:hover": { borderColor: "#459095" },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: 13, color: colors.grey[100] }} noWrap>
                        {r.zone_a} {(() => {
                          const DirIcon = DIRECTION_ICON[r.direction] || SyncAltIcon;
                          return <DirIcon sx={{ fontSize: 12, color: colors.grey[500], verticalAlign: "middle" }} />;
                        })()} {r.zone_b}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: colors.grey[400] }}>
                        {r.service ? r.service : "Any service"}
                        {r.note ? ` · ${r.note}` : ""}
                      </Typography>
                    </Box>
                    <Chip
                      label={r.allowed ? "Allowed" : "Not allowed"}
                      size="small"
                      sx={{ bgcolor: r.allowed ? `${ALLOWED_COLOR}22` : `${DENIED_COLOR}22`, color: r.allowed ? ALLOWED_COLOR : DENIED_COLOR, fontWeight: 700, fontSize: 10.5 }}
                    />
                    <IconButton size="small" onClick={() => startEdit(r)} sx={{ color: colors.grey[400], p: 0.4 }}>
                      <EditIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(id)} sx={{ color: colors.grey[400], p: 0.4 }}>
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          )}
          <Divider sx={{ mt: 2.5, borderColor: `${colors.grey[700]}55` }} />
        </Box>

        <Box ref={formSectionRef}>
          <SectionLabel>{editingId ? "Edit Rule" : "New Rule"}</SectionLabel>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Autocomplete
                freeSolo
                size="small"
                fullWidth
                options={zoneNames}
                value={form.zone_a}
                onChange={(e, v) => setForm((f) => ({ ...f, zone_a: v || "" }))}
                onInputChange={(e, v) => setForm((f) => ({ ...f, zone_a: v }))}
                renderInput={(params) => <TextField {...params} label="From zone" />}
              />
              <Autocomplete
                freeSolo
                size="small"
                fullWidth
                options={zoneNames}
                value={form.zone_b}
                onChange={(e, v) => setForm((f) => ({ ...f, zone_b: v || "" }))}
                onInputChange={(e, v) => setForm((f) => ({ ...f, zone_b: v }))}
                renderInput={(params) => <TextField {...params} label="To zone" />}
              />
            </Box>
            <TextField
              label="Service (optional — blank = any service)"
              size="small"
              fullWidth
              value={form.service}
              onChange={(e) => setForm((f) => ({ ...f, service: e.target.value }))}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: 12.5, color: colors.grey[300] }}>Not allowed</Typography>
                <Switch
                  checked={form.allowed}
                  onChange={(e) => setForm((f) => ({ ...f, allowed: e.target.checked }))}
                />
                <Typography sx={{ fontSize: 12.5, color: colors.grey[300] }}>Allowed</Typography>
              </Box>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={form.direction}
                onChange={(e, v) => v && setForm((f) => ({ ...f, direction: v }))}
                sx={{
                  ml: "auto",
                  "& .MuiToggleButton-root": { textTransform: "none", fontSize: 11, px: 1, py: 0.3, color: colors.grey[400], borderColor: colors.grey[800] },
                  "& .MuiToggleButton-root.Mui-selected": { color: colors.grey[100], bgcolor: `${colors.grey[700]}55` },
                }}
              >
                <ToggleButton value="both"><SyncAltIcon sx={{ fontSize: 14, mr: 0.5 }} />Both</ToggleButton>
                <ToggleButton value="a_to_b"><ArrowForwardIcon sx={{ fontSize: 14, mr: 0.5 }} />A → B</ToggleButton>
                <ToggleButton value="b_to_a"><ArrowBackIcon sx={{ fontSize: 14, mr: 0.5 }} />B → A</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <TextField
              label="Note"
              size="small"
              fullWidth
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            />
            {conflict && (
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", bgcolor: "#ff222218", border: `1px solid ${DENIED_COLOR}`, borderRadius: 1.5, p: 1.2 }}>
                <SyncAltIcon sx={{ fontSize: 16, color: DENIED_COLOR, mt: "1px", flexShrink: 0 }} />
                <Typography sx={{ fontSize: 11.5, color: colors.grey[100] }}>
                  Conflicts with an existing rule: <strong>{conflict.zone_a} ⇄ {conflict.zone_b}</strong>
                  {" "}({conflict.service || "any service"}) is already declared <strong>{conflict.allowed ? "allowed" : "not allowed"}</strong> —
                  {" "}remove or edit that rule first, or change the direction/service so they don't overlap.
                </Typography>
              </Box>
            )}
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              {editingId && <Button onClick={cancelEdit}>Cancel</Button>}
              <Button
                variant="contained"
                disableElevation
                disabled={!form.zone_a.trim() || !form.zone_b.trim() || !!conflict}
                onClick={handleSubmit}
              >
                {editingId ? "Save" : "Create Rule"}
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ backgroundColor: "#161616", borderTop: `1px solid ${colors.grey[800]}` }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConduitRuleManagerDialog;
